import { Link } from "wouter";
import { useCoiLinks } from "@/hooks/useCoiLinks";
import { useCoiDiscovered } from "@/hooks/useCoiDiscovered";
import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";

interface Props { coiId: string }

const CATEGORY_ROUTE: Record<string, string> = {
  "Client": "/clients",
  "Buyer Network": "/buyers",
  "Prospects": "/prospects",
  "COI": "/companies",
  "Capital Providers": "/capital-providers",
};

export function CoiNetworkStrip({ coiId }: Props) {
  const links = useCoiLinks(coiId).data ?? [];
  const discovered = useCoiDiscovered(coiId).data ?? [];

  const all = [
    ...links.map((l) => ({ id: l.companyId, name: l.companyName, category: l.companyCategory, manual: true })),
    ...discovered
      .filter((d) => !links.some((l) => l.companyId === d.companyId))
      .map((d) => ({ id: d.companyId, name: d.companyName, category: d.companyCategory, manual: false })),
  ];

  const breakdown = all.reduce<Record<string, number>>((acc, x) => {
    const key = x.category ?? "Other";
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});

  if (all.length === 0) {
    return (
      <div className="border rounded-lg p-5 bg-muted/20">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Network</h3>
        <p className="text-sm text-muted-foreground">
          No connections yet. Link a company below to start mapping this COI's network.
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-5">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-baseline gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Network</h3>
          <span className="text-sm font-medium">{all.length} Connected</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {Object.entries(breakdown).map(([cat, n]) => (
            <span key={cat}>{n} {cat}</span>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {all.slice(0, 12).map((x) => {
          const route = CATEGORY_ROUTE[x.category ?? ""] ?? "/clients";
          return (
            <Link key={x.id} href={`${route}/${x.id}`}>
              <Badge
                variant="outline"
                className={`whitespace-nowrap cursor-pointer hover:bg-muted/40 ${x.manual ? "border-primary/50" : "border-dashed"}`}
              >
                {x.name}
              </Badge>
            </Link>
          );
        })}
        {all.length > 12 && (
          <span className="text-xs text-muted-foreground whitespace-nowrap inline-flex items-center gap-1">
            +{all.length - 12} more <ArrowRight className="w-3 h-3" />
          </span>
        )}
      </div>
    </div>
  );
}
